import type {
    ApplicationSettingTheme,
    ApplicationSettingThemeColor,
    BlockAbstract,
    ChartType,
    DataSourceAbstract,
    Direction,
    ViewType
} from '@lighthouse/core'
import { BlockType, type FieldInputType } from '@lighthouse/core'
import { CHART_BLOCK_NAME_MAP } from '@lighthouse/shared'
import { FieldInputNameMap } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'

import { generateAudioBlock } from './generate/audio'
import { generateBreadcrumbBlock } from './generate/breadcrumb'
import { generateButtonBlock } from './generate/button'
import { generateCardBlock } from './generate/card'
import { generateChartBlock } from './generate/chart'
import { generateCollapseBlock } from './generate/collapse'
import { generateContainerBlock } from './generate/container'
import { generateDividerBlock } from './generate/divider'
import { generateFieldBlock } from './generate/field'
import { generateFieldGroupBlock } from './generate/fieldGroup'
import { generateFileBlock } from './generate/file'
import { generateFilterBlock } from './generate/filter'
import { generateFloatBlock } from './generate/float'
import { generateFormContainerBlock } from './generate/formContainer'
import { generateIconBlock } from './generate/icon'
import { generateIframeBlock } from './generate/iframe'
import { generateImageBlock } from './generate/image'
import { generateOnOffBlock } from './generate/onOff'
import { generateQrBarcodeBlock } from './generate/qrBarcode'
import { generateSubFormBlock } from './generate/subForm'
import { generateTabsBlock } from './generate/tabs'
import { generateTextBlock } from './generate/text'
import { generateTocBlock } from './generate/toc'
import { generateUserCenterBlock } from './generate/userCenter'
import { generateVideoBlock } from './generate/video'
import { generateViewBlock } from './generate/view'
import type { DefaultBlockType } from './type'

type GetInitBlockParams =
    | {
          type: Exclude<BlockType, 'container' | 'field' | 'view' | 'chart'> | DefaultBlockType
      }
    | {
          type: 'container'
          subType: Direction
      }
    | {
          type: 'field'
          subType: FieldInputType
      }
    | {
          type: 'view'
          subType: ViewType
      }
    | {
          type: 'chart'
          subType: ChartType
      }
    | {
          type: 'sync'
      }

export type BlockInitConfigOption = {
    appId?: string
    dataSource?: DataSourceAbstract
    theme?: ApplicationSettingTheme
}

/** 获取block初始配置 */
export function getBlockInitConfig(
    mainBreakPoint: string,
    item: GetInitBlockParams,
    option?: BlockInitConfigOption
): BlockAbstract | undefined {
    const { appId, dataSource, theme } = option ?? {}
    const id = `${item.type}-${nanoid(12)}`

    switch (item.type) {
        case BlockType.breadcrumb: {
            return {
                id,
                type: item.type,
                title: '面包屑',
                config: generateBreadcrumbBlock(mainBreakPoint)
            }
        }

        case BlockType.button: {
            return {
                id,
                type: item.type,
                title: '按钮',
                config: generateButtonBlock(mainBreakPoint),
                children: []
            }
        }

        case BlockType.card: {
            return {
                id,
                type: item.type,
                title: '图文导航',
                config: generateCardBlock(mainBreakPoint)
            }
        }

        case BlockType.chart: {
            return {
                id,
                type: item.type,
                title: CHART_BLOCK_NAME_MAP[item.subType],
                config: generateChartBlock(mainBreakPoint, item.subType)
            }
        }

        case BlockType.collapse: {
            return {
                id,
                type: item.type,
                title: '折叠面板',
                config: generateCollapseBlock(mainBreakPoint)
            }
        }

        case BlockType.container: {
            const config = generateContainerBlock(mainBreakPoint, item.subType)
            return {
                id,
                type: item.type,
                title: '容器',
                config,
                children: [{ id: config.viewList[0].id, children: [] }]
            }
        }

        case BlockType.divider: {
            return {
                id,
                type: item.type,
                title: '分割线',
                config: generateDividerBlock(mainBreakPoint)
            }
        }

        case BlockType.field: {
            return {
                id,
                type: item.type,
                title: FieldInputNameMap[item.subType],
                config: generateFieldBlock(mainBreakPoint, item.subType, theme)
            }
        }

        case BlockType.fieldGroup: {
            return {
                id,
                type: item.type,
                title: '字段组',
                config: generateFieldGroupBlock(mainBreakPoint)
            }
        }

        case BlockType.file: {
            return {
                id,
                type: item.type,
                title: '文件预览',
                config: generateFileBlock(mainBreakPoint)
            }
        }

        case BlockType.formContainer: {
            return {
                id,
                type: item.type,
                title: '表单容器',
                config: generateFormContainerBlock(mainBreakPoint, dataSource),
                children: []
            }
        }

        case BlockType.iframe: {
            return {
                id,
                type: item.type,
                title: '嵌入网页',
                config: generateIframeBlock(mainBreakPoint)
            }
        }

        case BlockType.image: {
            return {
                id,
                type: item.type,
                title: '图片',
                config: generateImageBlock(mainBreakPoint)
            }
        }

        case BlockType.tabs: {
            return {
                id,
                type: item.type,
                title: '标签栏',
                config: generateTabsBlock(mainBreakPoint)
            }
        }
        case BlockType.filter: {
            return {
                id,
                type: item.type,
                title: '筛选器',
                config: generateFilterBlock(mainBreakPoint, {
                    primaryPointer: theme?.primaryPointer || '',
                    palettes: theme?.palettes || [],
                    textPresetList: theme?.textPresetList || []
                })
            }
        }
        case BlockType.text: {
            return {
                id,
                type: item.type,
                title: '文本',
                config: generateTextBlock(mainBreakPoint),
                children: []
            }
        }
        case BlockType.video: {
            return {
                id,
                type: item.type,
                title: '视频',
                config: generateVideoBlock(mainBreakPoint)
            }
        }
        case BlockType.audio: {
            return {
                id,
                type: item.type,
                title: '音频',
                config: generateAudioBlock(mainBreakPoint)
            }
        }
        case BlockType.view: {
            return generateViewBlock(mainBreakPoint, { viewType: item.subType, id, appId, dataSource })
        }
        case BlockType.qrBarcode: {
            return {
                id,
                type: item.type,
                title: '条码',
                config: generateQrBarcodeBlock(mainBreakPoint)
            }
        }

        case BlockType.subForm: {
            return {
                id,
                type: item.type,
                title: '子表单',
                config: generateSubFormBlock(mainBreakPoint)
            }
        }
        case BlockType.icon: {
            return {
                id,
                type: item.type,
                title: '图标',
                config: generateIconBlock(mainBreakPoint),
                children: []
            }
        }
        case BlockType.userCenter: {
            return {
                id,
                type: item.type,
                title: '头像',
                config: generateUserCenterBlock(mainBreakPoint)
            }
        }
        case BlockType.floatBox: {
            return {
                id,
                type: item.type,
                title: '弹出窗口',
                config: generateFloatBlock(mainBreakPoint, 'hover', 'relative'),
                children: []
            }
        }
        case BlockType.onOff: {
            return {
                id,
                type: item.type,
                title: '开关按钮',
                config: generateOnOffBlock(mainBreakPoint)
            }
        }
        case BlockType.toc: {
            return {
                id,
                type: item.type,
                title: '笔记目录',
                config: generateTocBlock(mainBreakPoint)
            }
        }
        default: {
            break
        }
    }
}
